import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Star } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";

const LINES = ["Restore movement.", "Recover fully.", "Return to yourself."];

function MaskedLine({ children, index }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay: 0.35 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 lg:pt-40">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 pb-24 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-32">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-clay"
            data-testid="hero-overline"
          >
            <span className="h-px w-10 bg-clay" />
            {SITE.tagline} — {SITE.city}
          </motion.p>

          <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-tighter text-forest sm:text-6xl lg:text-7xl" data-testid="hero-heading">
            {LINES.map((line, i) => (
              <MaskedLine key={line} index={i}>
                {i === 2 ? <em className="text-pine">{line}</em> : line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-md text-base leading-relaxed text-forest/70"
            data-testid="hero-subtext"
          >
            Deep tissue, sports & relaxation massage in Vancouver — practising
            at West End Physiotherapy on Alberni and Paper Crane Massage Therapy
            on East Hastings. Evidence-informed care, unhurried appointments,
            direct billing to most insurers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href={SITE.locations[0].bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-west-end"
              className="group inline-flex items-center gap-3 rounded-full bg-pine px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone shadow-lg shadow-pine/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/30"
            >
              Book — West End
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={SITE.locations[1].bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-east-hastings"
              className="group inline-flex items-center gap-3 rounded-full border border-pine/30 px-8 py-4 text-xs uppercase tracking-[0.2em] text-pine transition-all duration-300 hover:-translate-y-1 hover:border-pine hover:bg-pine/5"
            >
              Book — East Hastings
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-treatments-link"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="text-xs uppercase tracking-[0.2em] text-forest underline decoration-clay underline-offset-8 transition-colors duration-300 hover:text-clay"
            >
              Explore treatments
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-4 text-xs uppercase tracking-[0.2em] text-forest/60"
            data-testid="hero-trust-bar"
          >
            <span className="flex items-center gap-2">
              <Star className="h-3.5 w-3.5 fill-clay text-clay" /> 5.0 on Google
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> West End & East Van
            </span>
            <span>Langara College RMT grad</span>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-t-[999px]"
          >
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Registered massage therapist providing treatment in a calm Vancouver studio"
              data-testid="hero-image"
              className="h-[480px] w-full scale-110 object-cover lg:h-[600px]"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-serif text-3xl italic text-pine lg:text-4xl"
            data-testid="hero-signature"
          >
            {SITE.name}, {SITE.credential}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
